import React, { useState } from 'react';
import { User } from '../../types';
import { 
  Card, 
  CardContent, 
  Typography, 
  Button, 
  Box, 
  Avatar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Chip,
  Skeleton,
  MobileStepper,
  IconButton,
  useMediaQuery
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WorkIcon from '@mui/icons-material/Work';
import SchoolIcon from '@mui/icons-material/School';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VerifiedIcon from '@mui/icons-material/Verified';
import HeightIcon from '@mui/icons-material/Height';
import FlagIcon from '@mui/icons-material/Flag';
import PersonIcon from '@mui/icons-material/Person';
import SendIcon from '@mui/icons-material/Send';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

interface ProfileCardProps {
  profile?: User;  // Make profile optional
  onMatchRequest: (profileId: string, message: string) => void;
  isMatchRequestPage?: boolean;  // Add this prop
}

const ethnicityMap: { [key: string]: string } = {
  "black": "Black",
  "eastAsian": "East Asian",
  "white": "White",
  "middleEastern": "Middle Eastern",
  "southAsian": "South Asian",
  "latinAmerican": "Hispanic/Latino",
  "indigenous": "Indigenous",
  "other": "Other"
};

const colorMap: { [key: string]: string } = {
  "black": " rgb(0, 0, 0)",
  "eastAsian": " rgb(218, 204, 14)",
  "white": " rgb(233, 226, 214)",
  "middleEastern": " rgb(170, 115, 19)",
  "southAsian": " rgb(121, 66, 52)",
  "latinAmerican": " rgb(218, 174, 148)",
  "indigenous": " rgb(216, 100, 38)",
  "other": "rgb(177, 31, 140)"
};

const ProfileCard: React.FC<ProfileCardProps> = ({ profile, onMatchRequest, isMatchRequestPage }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const isMobile = useMediaQuery('(max-width:600px)');
  const photos = profile ? [profile.profilePicture, ...(profile.additionalPhotos || [])] : [];

  // If no profile is provided, show a loading skeleton
  if (!profile) {
    return (
      <Card sx={{ 
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 3
      }}>
        <Skeleton variant="rectangular" height={300} />
        <CardContent>
          <Skeleton variant="text" height={60} />
          <Skeleton variant="text" />
          <Skeleton variant="text" />
          <Skeleton variant="text" width="60%" />
        </CardContent>
      </Card>
    );
  }

  const handleSendRequest = () => {
    onMatchRequest(profile.uid, message);
    setMessage('');
    setOpen(false);
  };

  // Safely handle interests splitting
  const interests = profile.interests
    ? profile.interests.split(',').map(i => i.trim()).filter(Boolean)
    : [];

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  return (
    <>
      <Card sx={{ 
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 3
      }}>
        <Box sx={{ 
          position: 'relative',
          paddingTop: '133%',
          bgcolor: 'grey.100'
        }}>
          <Avatar
            src={photos[activeStep]}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: 0,
              objectFit: 'cover'
            }}
          />
          {photos.length > 1 && (
            <MobileStepper
              steps={photos.length}
              position="static"
              activeStep={activeStep}
              sx={{
                position: 'absolute',
                bottom: 0,
                width: '97%',
                bgcolor: 'rgba(0, 0, 0, 0)',
                opacity: 1
              }}
              nextButton={
                <IconButton
                  size="medium"
                  onClick={handleNext}
                  disabled={activeStep === photos.length - 1}
                  sx={{ color: 'white', bgcolor: 'green' }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              }
              backButton={
                <IconButton
                  size="medium"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  sx={{ color: 'white' ,
                    bgcolor: 'green'
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
              }
            />
          )}
        </Box>
        
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h4" component="h2">
              {profile.displayName || 'Anonymous'}{profile.age && `, ${profile.age}`}
            </Typography>
            {profile.verificationStatus === 'verified' && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 0.5,
                  bgcolor: 'success.light',
                  color: 'success.dark',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  ml: 1
                }}
              >
                <VerifiedIcon sx={{ fontSize: 20 }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 'medium',
                    lineHeight: 1,
                    color: 'white'
                  }}
                >
                  US Citizen
                </Typography>
              </Box>
            )}
          </Box>
          
          

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocationOnIcon  sx={{ color: 'rgb(212, 21, 21)' }} />
            <Typography variant="body1">
              {profile.location || 'Location not specified'}
            </Typography>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeightIcon color="action" />
            <Typography variant="body1">
              {profile.height ? `${Math.floor(Number(profile.height) / 12)}'${Number(profile.height) % 12}"` : 'Height not specified'}
            </Typography>
          </Box>

          {profile.ethnicities && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ color: colorMap[profile.ethnicities[0]] }} />
              <Typography variant="body1">
                {profile.ethnicities.map(e => ethnicityMap[e]).join(', ')}
              </Typography>
            </Box>
          )}

          {profile.occupation && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon sx={{ color: 'rgb(114, 64, 25)' }} />
              <Typography variant="body1">{profile.occupation}</Typography>
            </Box>
          )}

          {profile.education && (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon sx={{ color: 'black' }} />
              <Typography variant="body1">{profile.education}</Typography>
            </Box>
          )}

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            About Me
          </Typography>
          
          <Typography variant="body1" paragraph>
            {profile.bio || 'No bio provided'}
          </Typography>

          {interests.length > 0 && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Interests
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {interests.map((interest, index) => (
                  <Chip 
                    key={index} 
                    label={interest} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}

          <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FavoriteIcon sx={{ color: 'red' }}/>
            <Typography variant="body1">
              Looking for {profile.lookingFor || 'Not specified'}
            </Typography>
          </Box>

        
        </CardContent>

        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
          {!isMatchRequestPage && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => setOpen(true)}
              fullWidth
              size="large"
              sx={{ color: 'white' }}
            >
              Send Match Request
            </Button>
          )}
        </Box>
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm'>
        <DialogTitle>Send Match Request to {profile.displayName || 'User'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Add a message"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write a friendly message to introduce yourself..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSendRequest} variant="contained" color="primary">
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
              Send Request
            </Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileCard;